/**
 * Sign-in, start to finish.
 *
 * The API moves a session through stages — password, then (for instructors, or anyone
 * who turned it on) a second factor or first-time authenticator setup, then a forced
 * password change after an admin reset. This page walks through whichever apply, and
 * resumes at the right one if the student reloads half-way.
 */

import { WEB_PAGES } from '@praxadon/shared/pages';
import { Button, Field, Input } from '@praxadon/ui';
import { type SubmitEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { encode } from 'uqr';

import { ApiError, describeError } from '../../api/client.ts';
import { type SessionStage, auth } from '../../api/endpoints.ts';
import { useSession } from '../../app/session.tsx';
import { AuthLayout, FormError } from './AuthLayout.tsx';
import { PasswordInput, passwordHint } from './PasswordInput.tsx';
import { safeNext } from './next.ts';
import { Turnstile, type TurnstileHandle } from './Turnstile.tsx';

type Step = SessionStage | 'credentials' | 'recovery-codes';

export function LoginPage() {
  const { site, session, refresh } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [step, setStep] = useState<Step>(() =>
    session?.authenticated ? session.stage : 'credentials',
  );
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  // Where sign-in continues once the recovery codes are saved: straight in, or first a
  // new password (an instructor whose password was reset enrols, then changes it).
  const [afterCodes, setAfterCodes] = useState<SessionStage>('active');

  useEffect(() => {
    if (step !== 'active') return;
    void refresh().then(() => navigate(next, { replace: true }));
  }, [step, refresh, navigate, next]);

  if (site.kind !== 'tenant') return <PlatformLoginNotice />;
  if (site.tenant.status !== 'active') return <TenantUnavailable status={site.tenant.status} />;

  const branding = site.tenant.branding;
  switch (step) {
    case 'credentials':
    case 'active':
      return (
        <AuthLayout
          title={branding.loginHeading ?? 'Sign in'}
          subtitle={branding.loginSubheading ?? 'Welcome back! Sign in to keep building.'}
          footer={
            site.tenant.onboardingMode === 'off' ? (
              'New here? Ask your instructor to invite you.'
            ) : (
              <>
                New here?{' '}
                <Link className="font-medium text-fg underline" to={WEB_PAGES.signup}>
                  Create your account
                </Link>
              </>
            )
          }
        >
          <CredentialsForm turnstileSiteKey={site.turnstileSiteKey} onStage={setStep} />
        </AuthLayout>
      );
    case 'totp_required':
      return (
        <AuthLayout
          title="Two-step check"
          subtitle="Enter the 6-digit code from your authenticator app."
        >
          <SecondFactorForm onStage={setStep} />
        </AuthLayout>
      );
    case 'totp_enrol':
      return (
        <AuthLayout
          title="Set up two-step sign-in"
          subtitle="Instructor accounts need a second step, so nobody can sign in with just a password."
        >
          <EnrolForm
            onDone={(codes, stage) => {
              // The codes are shown whatever comes next: this is the only time they exist.
              setRecoveryCodes(codes);
              setAfterCodes(stage);
              setStep('recovery-codes');
            }}
          />
        </AuthLayout>
      );
    case 'recovery-codes':
      return (
        <AuthLayout
          form={false}
          title="Save your recovery codes"
          subtitle="Each code signs you in once if you lose your phone."
        >
          <RecoveryCodes
            codes={recoveryCodes}
            onContinue={() => {
              setStep(afterCodes);
            }}
          />
        </AuthLayout>
      );
    case 'password_change':
      return (
        <AuthLayout
          title="Choose a new password"
          subtitle="Your password was reset, so please pick a new one to continue."
        >
          <ChangePasswordForm onStage={setStep} />
        </AuthLayout>
      );
  }
}

function CredentialsForm({
  turnstileSiteKey,
  onStage,
}: {
  turnstileSiteKey: string | null;
  onStage: (stage: SessionStage) => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const turnstile = useRef<TurnstileHandle>(null);

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await auth.login({ username, password, turnstileToken: token });
      onStage(result.stage);
    } catch (failure) {
      setError(describeError(failure));
      setPassword('');
      turnstile.current?.reset();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4" noValidate>
      <FormError message={error} />
      <Field label="Username">
        <Input
          name="username"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
          value={username}
          onChange={(event) => {
            setUsername(event.target.value);
          }}
        />
      </Field>
      <Field
        label="Password"
        hint={
          <Link className="underline hover:text-fg" to={WEB_PAGES.forgotPassword}>
            Forgot your password?
          </Link>
        }
      >
        <PasswordInput
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
      </Field>
      {turnstileSiteKey ? (
        <Turnstile ref={turnstile} siteKey={turnstileSiteKey} onToken={setToken} />
      ) : null}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        loading={busy}
        disabled={!username || !password || (turnstileSiteKey !== null && !token)}
      >
        Sign in
      </Button>
    </form>
  );
}

function SecondFactorForm({ onStage }: { onStage: (stage: SessionStage) => void }) {
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await auth.verifyTotp(code.trim());
      onStage(result.stage);
    } catch (failure) {
      setError(describeError(failure));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4">
      <FormError message={error} />
      <Field
        label={recovery ? 'Recovery code' : 'Authentication code'}
        hint={recovery ? 'One of the codes you saved when you set up two-step sign-in.' : undefined}
      >
        <Input
          name="code"
          autoComplete="one-time-code"
          inputMode={recovery ? 'text' : 'numeric'}
          autoFocus
          spellCheck={false}
          value={code}
          onChange={(event) => {
            setCode(event.target.value);
          }}
          className="text-center font-mono text-lg tracking-widest"
        />
      </Field>
      <Button
        type="submit"
        variant="primary"
        size="lg"
        loading={busy}
        disabled={code.trim().length < 6}
      >
        Continue
      </Button>
      <button
        type="button"
        className="text-sm text-fg-muted underline hover:text-fg"
        onClick={() => {
          setRecovery((r) => !r);
          setCode('');
        }}
      >
        {recovery ? 'Use my authenticator app instead' : 'Lost your phone? Use a recovery code'}
      </button>
      <SignOutLink />
    </form>
  );
}

/** An otpauth URI drawn as a QR code, in the page: the secret never leaves the browser. */
function QrCode({ value }: { value: string }) {
  const { data, size } = encode(value, { border: 2 });
  let path = '';
  data.forEach((row, y) => {
    row.forEach((dark, x) => {
      if (dark) path += `M${x} ${y}h1v1h-1z`;
    });
  });
  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className="mx-auto size-48 rounded-lg bg-white p-1"
      role="img"
      aria-label="QR code for your authenticator app"
      shapeRendering="crispEdges"
    >
      <path d={path} fill="#000" />
    </svg>
  );
}

function EnrolForm({ onDone }: { onDone: (codes: string[], stage: SessionStage) => void }) {
  const [enrolment, setEnrolment] = useState<{ uri: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    auth.enrolTotp().then(
      (result) => {
        if (!cancelled) setEnrolment(result);
      },
      (failure: unknown) => {
        if (!cancelled) setError(describeError(failure));
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await auth.confirmTotp(code.trim());
      onDone(result.recoveryCodes, result.stage);
    } catch (failure) {
      setError(describeError(failure));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4">
      <FormError message={error} />
      <ol className="list-decimal space-y-1 pl-5 text-sm text-fg-muted">
        <li>
          Install an authenticator app, such as Google Authenticator or Microsoft Authenticator.
        </li>
        <li>Scan this code with it.</li>
        <li>Type the 6-digit code it shows.</li>
      </ol>
      {enrolment ? (
        <>
          <QrCode value={enrolment.uri} />
          <details className="text-xs text-fg-muted">
            <summary className="cursor-pointer">Can't scan? Enter this key instead</summary>
            <code className="mt-2 block rounded bg-surface-sunken p-2 font-mono break-all text-fg">
              {enrolment.secret}
            </code>
          </details>
        </>
      ) : null}
      <Field label="Code from the app">
        <Input
          name="code"
          autoComplete="one-time-code"
          inputMode="numeric"
          value={code}
          onChange={(event) => {
            setCode(event.target.value);
          }}
          className="text-center font-mono text-lg tracking-widest"
        />
      </Field>
      <Button
        type="submit"
        variant="primary"
        size="lg"
        loading={busy}
        disabled={code.trim().length !== 6}
      >
        Turn on two-step sign-in
      </Button>
      <SignOutLink />
    </form>
  );
}

function RecoveryCodes({ codes, onContinue }: { codes: string[]; onContinue: () => void }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');

  function download() {
    const url = URL.createObjectURL(new Blob([`${text}\n`], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'praxadon-recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-fg-muted">
        Keep these somewhere safe, like a password manager. They are shown only once.
      </p>
      <ul
        className="grid grid-cols-2 gap-2 rounded-lg bg-surface-sunken p-3 font-mono text-sm text-fg"
        aria-label="Recovery codes"
      >
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Button
          className="flex-1"
          onClick={() => {
            void navigator.clipboard.writeText(text).then(() => {
              setCopied(true);
            });
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button className="flex-1" onClick={download}>
          Download
        </Button>
      </div>
      <label className="flex items-center gap-2 text-sm text-fg">
        <input
          type="checkbox"
          checked={saved}
          onChange={(event) => {
            setSaved(event.target.checked);
          }}
          className="size-4 accent-[var(--brand)]"
        />
        I have saved my recovery codes
      </label>
      <Button variant="primary" size="lg" disabled={!saved} onClick={onContinue}>
        Continue
      </Button>
    </div>
  );
}

function ChangePasswordForm({ onStage }: { onStage: (stage: SessionStage) => void }) {
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldError(undefined);
    try {
      const result = await auth.changePassword({ currentPassword: current, newPassword: password });
      onStage(result.stage);
    } catch (failure) {
      if (failure instanceof ApiError && failure.fields['newPassword'])
        setFieldError(failure.fields['newPassword']);
      else setError(describeError(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4">
      <FormError message={error} />
      <Field label="Current password" hint="The temporary password your instructor gave you.">
        <PasswordInput
          autoComplete="current-password"
          value={current}
          onChange={(event) => {
            setCurrent(event.target.value);
          }}
        />
      </Field>
      <Field label="New password" hint={passwordHint(password)} error={fieldError}>
        <PasswordInput
          autoComplete="new-password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
      </Field>
      <Button
        type="submit"
        variant="primary"
        size="lg"
        loading={busy}
        disabled={!current || !password}
      >
        Save and continue
      </Button>
      <SignOutLink />
    </form>
  );
}

function SignOutLink() {
  const { refresh } = useSession();
  return (
    <button
      type="button"
      className="text-sm text-fg-subtle hover:text-fg"
      onClick={() => {
        void auth.logout().finally(() => {
          void refresh().then(() => {
            window.location.assign(WEB_PAGES.login);
          });
        });
      }}
    >
      Not you? Sign out
    </button>
  );
}

function TenantUnavailable({ status }: { status: 'suspended' | 'banned' }) {
  return (
    <AuthLayout
      form={false}
      title={status === 'suspended' ? 'This class is paused' : 'This class is closed'}
      subtitle={
        status === 'suspended'
          ? 'Your instructor or the platform has paused this class for now. Your work is safe — please check back later.'
          : 'This class is no longer available. If you think this is a mistake, contact your instructor.'
      }
    >
      <p className="text-center text-sm text-fg-muted">Nothing to do here right now.</p>
    </AuthLayout>
  );
}

function PlatformLoginNotice() {
  return (
    <AuthLayout
      form={false}
      title="Every class has its own address"
      subtitle="Sign in at your class's address, which looks like yourclass.ide.praxadon.com. Your instructor will have given it to you."
    >
      <p className="text-center text-sm text-fg-muted">Can't find it? Ask your instructor.</p>
    </AuthLayout>
  );
}
